import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";
import CheckBoxLabelHookForm from "../../../../../components/CheckBoxLabelHookForm";
import SelectHookFormSingle from "../../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../../components/TextInputLabelHookForm";
import useGetOfficeBank from "../../OfficeBank/hooks/useGetOfficeBank";
import { editStateOfficeAccount } from "../atoms";
import { OfficeAccountType } from "../types";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";

export default function OfficeAccountDetails(props: {
  setChanges: React.Dispatch<React.SetStateAction<boolean>>;
  data: OfficeAccountType | undefined;
}) {
  // Props drilled
  const { data, setChanges } = props;
  
  // Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const deletePermission = userPermissions.includes(PERMISSIONS.permission_utilities_officeconfiguration_officeaccount_delete);

  // Recoil Hook
  const edit = useRecoilValue(editStateOfficeAccount);

  // Form Functions
  const { register } = useFormContext();
  const formMethods = useFormContext();

  // Local Hooks
  const [inputData, setInputData] = useState(data?.accountNumber || "");
  const [checkBoxChanges, setCheckBoxChanges] = useState(data?.isActive);
  const [dropBoxChanges, setDropBoxChanges] = useState(
    data?.officeBankId || ""
  );

  const { data: bankTypes, isLoading: businessTypeLoading } =
    useGetOfficeBank();
  const bankTypeOptions = bankTypes
    ?.filter((x) => x.isActive === true)
    .map((x) => {
      return {
        label: x.bankName,
        value: x.bankId,
      };
    });

  const allBankTypeOpt = bankTypeOptions && [...bankTypeOptions];
  return (
    <>
      <div className="bg-bgDB">
        <div className="m-2 bg-white box-shadow h-full">
          <div className="grid grid-cols-1 md:grid-cols-3 p-2">
            <div className="my-1 mx-3">
              <TextInputLabelHookForm
                name="accountNumber"
                label="Office Account"
                type="text"
                onChange={(e) => {
                  setInputData(e.target.value);
                  if (
                    e.target.value.length === 0 ||
                    dropBoxChanges === "" ||
                    (e.target.value === data?.accountNumber &&
                      checkBoxChanges === data?.isActive &&
                      dropBoxChanges === data?.officeBankId)
                  ) {
                    setChanges(false);
                  } else {
                    setChanges(true);
                  }
                  e.target.value = e.target.value.replace(/\s+/g, " ");
                }}
                required={true}
                className={
                  formMethods.formState.errors.accountNumber &&
                  formMethods.formState.errors.accountNumber.message &&
                  "border-2 border-red-500 col-span-2 "
                }
                register={formMethods.register("accountNumber", {
                  required: {
                    value: true,
                    message: "Required Field",
                  },
                })}
                maxLength={255}
              />
            </div>

            <div className="my-1 mx-3">
              <Controller
                name="officeBankId"
                control={formMethods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    placeHolder={data?.officeBankName}
                    isClearable={false}
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                      setDropBoxChanges(e.value);
                      if (
                        e.value === 0 ||
                        inputData === "" ||
                        (e.value === data?.officeBankId &&
                          checkBoxChanges === data?.isActive &&
                          inputData === data?.accountNumber)
                      ) {
                        setChanges(false);
                      } else {
                        setChanges(true);
                      }
                    }}
                    value={field.value}
                    isLoading={businessTypeLoading}
                    options={allBankTypeOpt}
                    label="Office Bank"
                    className={
                      formMethods.formState.errors.state &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                  />
                )}
              />
            </div>

            {edit && (
              <div className="my-1 mx-3">
                <CheckBoxLabelHookForm
                  name="isActive"
                  label={
                    checkBoxChanges
                      ? "Uncheck to Deactivate"
                      : "Check to Activate"
                  }
                  type="checkbox"
                  validation={{
                    required: {
                      value: false,
                      message: "Required Field",
                    },
                  }}
                  onClick={(e) => {
                    if ((!deletePermission && data?.isActive)) return;
                    
                    setCheckBoxChanges(e.target.checked);

                    if (
                      inputData?.length === 0 ||
                      (e.target.checked === data?.isActive &&
                        inputData === data?.accountNumber &&
                        dropBoxChanges === data?.officeBankId)
                    ) {
                      setChanges(false);
                    } else {
                      setChanges(true);
                    }
                  }}
                  disabled={(!deletePermission && data?.isActive) ? true : false}
                  register={register}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
